// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import type { Project } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import type { ColDef } from 'ag-grid-enterprise';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { CellRenderer } from 'components/Mantine/CellRenderer';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import { CustomSelectComponent } from 'helpers/ag-grid/CustomSelectComponent';
import { ActiveClosedStatusBadge } from 'modules/Admin/Facilities/ActiveClosedStatusBadge';
import { EditProjectMenu } from 'modules/Admin/Projects/EditProjectMenu';

export const getProjectsColDef = ({
  deleteProject,
  statusChangeProject,
}: {
  deleteProject: (project: Project) => void;
  statusChangeProject: (project: Project) => void;
}): ColDef<Project>[] => [
  {
    headerName: 'Project Id',
    field: 'projectIdentifier',
    width: 120,
  },
  {
    field: 'projectName',
    lockVisible: true,
    flex: 3,
    minWidth: 150,
    cellRenderer: CellRenderer<Project, 'projectName'>(({ data, node }) => (
      <Text className="link" onClick={() => node.setSelected(true, true)}>
        {data.projectName}
      </Text>
    )),
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    headerName: 'Project Type',
    flex: 2,
    minWidth: 150,
    field: 'projectType',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    headerName: 'Default Facility',
    field: 'defaultFacilityName',
    flex: 2,
    minWidth: 150,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    headerName: 'Status',
    field: 'status',
    colId: 'status',
    flex: 1,
    minWidth: 100,
    sortable: true,
    cellRenderer: CellRenderer<Project, 'status'>(({ value: status }) => (
      <ActiveClosedStatusBadge isClosed={status === 'Closed'} />
    )),
  },
  {
    headerName: 'City',
    field: 'city',
    colId: 'city',
    width: 150,
    hide: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    headerName: 'State',
    field: 'state',
    colId: 'state',
    width: 120,
    hide: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    headerName: 'Zip Code',
    field: 'postalCode',
    colId: 'postalCode',
    width: 140,
    hide: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    headerName: 'ACC',
    field: 'hasLinkedModels',
    headerTooltip: 'Indicates ACC model is linked to project',
    cellRenderer: CellRenderer<Project, 'hasLinkedModels'>(({ data: project }) =>
      project.hasLinkedModels ? <EvolveIcon icon="Link" light /> : null,
    ),
    width: 110,
    filter: CustomSelectComponent({
      label: 'Linked ACC',
      defaultValue: '',
      data: [
        { value: '', label: 'All projects' },
        { value: 'true', label: 'With linked models' },
        { value: 'false', label: 'Without linked models' },
      ],
      filterKey: 'hasLinkedModels',
    }),
    sortable: false,
  },
  {
    ...lockedColDef('right'),
    width: 64,
    cellRenderer: CellRenderer<Project>(({ data, node }) => (
      <EditProjectMenu
        selectProject={(project) => {
          node.updateData(project);
          node.setSelected(true, true);
        }}
        deleteProject={deleteProject}
        statusChangeProject={statusChangeProject}
        project={data}
      />
    )),
  },
];
